'use client';

import Link, { type LinkProps } from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import {
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type TouchEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  getLinkPrefetchProp,
  type PrefetchMode,
  shouldSchedulePrefetch,
} from '@/lib/prefetch-policy';
import { cn } from '@/lib/utils';

type SmartLinkProps = LinkProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    authenticated?: boolean;
    children: ReactNode;
    mode?: PrefetchMode;
  };

const prefetchedHrefs = new Set<string>();
const queuedTasks: Array<() => void> = [];
let activePrefetches = 0;
const MAX_CONCURRENT_PREFETCHES = 2;
const GUARDED_PREFETCH_DELAY = 1200;

function getConnectionInfo() {
  if (typeof navigator === 'undefined') {
    return {};
  }

  const connection = (
    navigator as Navigator & {
      connection?: { effectiveType?: string; saveData?: boolean };
    }
  ).connection;

  return {
    effectiveType: connection?.effectiveType,
    saveData: connection?.saveData,
  };
}

function enqueuePrefetch(task: () => void) {
  queuedTasks.push(task);
  flushPrefetchQueue();
}

function flushPrefetchQueue() {
  while (activePrefetches < MAX_CONCURRENT_PREFETCHES && queuedTasks.length > 0) {
    const nextTask = queuedTasks.shift();

    if (!nextTask) {
      return;
    }

    activePrefetches += 1;
    nextTask();
  }
}

function markPrefetchComplete() {
  activePrefetches = Math.max(0, activePrefetches - 1);
  flushPrefetchQueue();
}

function isExternalHref(href: string) {
  return /^(https?:)?\/\//.test(href);
}

function scheduleWhenIdle(callback: () => void, timeout = 1500) {
  if (typeof window === 'undefined') {
    return;
  }

  const requestIdle = window.requestIdleCallback;

  if (requestIdle) {
    requestIdle(() => callback(), { timeout });
    return;
  }

  window.setTimeout(callback, Math.min(timeout, 300));
}

export function SmartLink({
  authenticated = false,
  children,
  href,
  mode = 'always',
  className,
  onClick,
  onFocus,
  onMouseEnter,
  onTouchStart,
  prefetch,
  target,
  ...props
}: SmartLinkProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [isNavigating, setIsNavigating] = useState(false);
  const hasAutoPrefetchedRef = useRef(false);
  const hrefString = useMemo(
    () => (typeof href === 'string' ? href : null),
    [href],
  );
  const isExternal = hrefString ? isExternalHref(hrefString) : false;
  const hrefPathname = useMemo(() => {
    if (!hrefString || isExternal) return null;
    try {
      return new URL(hrefString, 'http://suppr.local').pathname;
    } catch {
      return hrefString.split('?')[0] || null;
    }
  }, [hrefString, isExternal]);

  const canPrefetch = shouldSchedulePrefetch(mode, {
    effectiveType: getConnectionInfo().effectiveType,
    isAuthenticated: authenticated,
    isExternal: isExternal || target === '_blank',
    saveData: getConnectionInfo().saveData,
  });

  const linkPrefetch = prefetch ?? getLinkPrefetchProp(mode, { canPrefetch });

  const runPrefetch = () => {
    if (!hrefString || !canPrefetch || prefetchedHrefs.has(hrefString)) {
      return;
    }

    prefetchedHrefs.add(hrefString);

    enqueuePrefetch(() => {
      Promise.resolve(router.prefetch(hrefString))
        .catch(() => {
          prefetchedHrefs.delete(hrefString);
        })
        .finally(() => {
          markPrefetchComplete();
        });
    });
  };

  useEffect(() => {
    if (mode !== 'guarded' || !canPrefetch || hasAutoPrefetchedRef.current) {
      return;
    }

    hasAutoPrefetchedRef.current = true;

    scheduleWhenIdle(runPrefetch, GUARDED_PREFETCH_DELAY);
  }, [canPrefetch, mode]);

  const handleIntentPrefetch = () => {
    if (mode === 'intent') {
      runPrefetch();
    }
  };

  const handleMouseEnter = (event: MouseEvent<HTMLAnchorElement>) => {
    onMouseEnter?.(event);
    handleIntentPrefetch();
  };

  const handleTouchStart = (event: TouchEvent<HTMLAnchorElement>) => {
    onTouchStart?.(event);
    handleIntentPrefetch();
  };

  const handleFocus = (event: React.FocusEvent<HTMLAnchorElement>) => {
    onFocus?.(event);
    handleIntentPrefetch();
  };

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      setIsNavigating(false);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [pathname]);

  useEffect(() => {
    if (!isNavigating) return;
    const timer = window.setTimeout(() => setIsNavigating(false), 10_000);
    return () => window.clearTimeout(timer);
  }, [isNavigating]);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!hrefString || isExternal || target === '_blank') return;
    if (hrefPathname && hrefPathname === pathname) return;
    setIsNavigating(true);
  };

  return (
    <Link
      href={href}
      {...props}
      aria-busy={isNavigating || props['aria-busy'] ? true : undefined}
      className={cn(className, isNavigating && 'cursor-wait opacity-70')}
      data-pending={isNavigating ? '' : undefined}
      onClick={handleClick}
      onFocus={handleFocus}
      onMouseEnter={handleMouseEnter}
      onTouchStart={handleTouchStart}
      prefetch={linkPrefetch}
      target={target}
    >
      {children}
    </Link>
  );
}
